import { useQuery } from '@tanstack/react-query';
import { Clock, FileCheck2, Headphones, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { listCatalog } from '../api';

export default function CauCatalogPage() {
  const [params] = useSearchParams();
  const assetId = params.get('assetId');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const query = useQuery({
    queryKey: ['cau', 'catalog', search, categoryId],
    queryFn: () => listCatalog(search, categoryId),
  });
  const categories = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of query.data ?? []) map.set(item.category.id, item.category.name);
    return [...map.entries()];
  }, [query.data]);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <span className="rounded-xl bg-blue-100 p-2 text-blue-700"><Headphones className="h-6 w-6" /></span>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Central de Atendimento</h1>
            <p className="text-sm text-gray-600">Escolha o serviço que melhor descreve sua necessidade.</p>
          </div>
        </div>
      </div>

      <div className="mb-6 grid gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-[1fr_260px]">
        <label className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            placeholder="Buscar serviço por nome, descrição ou código"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <select
          className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          <option value="">Todas as categorias</option>
          {categories.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {query.isLoading && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[1, 2, 3].map((n) => <Skeleton key={n} className="h-56" />)}</div>}
      {query.isError && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">Não foi possível carregar o catálogo.</div>}
      {!query.isLoading && !query.isError && (query.data?.length ?? 0) === 0 && (
        <EmptyState title="Nenhum serviço disponível" description="Ajuste os filtros ou procure a equipe responsável." />
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {query.data?.map((service) => (
          <Link
            key={service.id}
            to={`/cau/catalogo/${service.id}${assetId ? `?assetId=${encodeURIComponent(assetId)}` : ''}`}
            className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">{service.category.name}</span>
              <span className="font-mono text-xs text-gray-400">{service.code}</span>
            </div>
            <h2 className="text-base font-semibold text-gray-900 group-hover:text-blue-700">{service.name}</h2>
            <p className="mt-2 line-clamp-3 text-sm text-gray-600">{service.description}</p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{service.sla.applicable ? `${service.sla.resolutionMinutes ?? '—'} min` : 'SLA não aplicável'}</span>
              <span className="flex items-center gap-1"><FileCheck2 className="h-3.5 w-3.5" />{service.approvalRequired ? 'Requer aprovação' : 'Sem aprovação'}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

